import React, { useEffect, useState } from "react";
import {
  SectionCard,
  FormRow,
  FieldInput,
  PrimaryButton,
  SecondaryButton
} from "@/components/ds";
import { ArrowLeft, Check } from "@/components/ds/icons";
import useApi from "@/hooks/useApi";
import useTeams from "@/hooks/useTeams";
import { useLocation, useNavigate } from "react-router-dom";
import { ensureDefaultRoutingWorkflow } from "@/pages/Messaging/Workflows/defaultWorkflowHelper";
import LoadingSpinner from "@/components/LoadingSpinner";
const ShopifyLogo = "/img/shopify.svg";

interface Props {
  appStatus?: string;
  /**
   * Optional overrides for the three navigate-away points in this component.
   * When omitted, behavior is IDENTICAL to the original standalone-route
   * behavior (falls back to the `returnTo=onboarding` query-param convention
   * below, then to the classic `/integrations/...` destinations). This lets
   * the Solution Packages wizard hand off to the real, working Shopify OAuth
   * round-trip (full-page redirect to Shopify and back) without forking the
   * component — see PackageSetupWizard.tsx's "commerce-store" step.
   */
  onCancel?: () => void;
  onFinish?: () => void;
  onAdvancedSettings?: () => void;
}

/** sessionStorage key shared with PackageSetupWizard/Packages index — survives
 * the full-page redirect to Shopify's OAuth consent screen and back. */
const PACKAGE_WIZARD_RESUME_KEY = "velaro.packageWizardResume";

export default function ShopifyCreate({
  onCancel,
  onFinish,
  onAdvancedSettings
}: Props) {
  const [storeName, setStoreName] = useState("");
  const [domain, setDomain] = useState("");
  const [configId, setConfigId] = useState<number>(-1);
  const [step, setStep] = useState(1);
  const [sending, setSending] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [defaultWorkflowCreated, setDefaultWorkflowCreated] = useState(false);
  const [selectedTeamId, setSelectedTeamId] = useState<number | "">("");
  const [savingTeam, setSavingTeam] = useState(false);

  const api = useApi();
  const { teams } = useTeams();
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  // The `returnTo` query param is present on the way OUT to Shopify's OAuth
  // consent screen, but Shopify's post-consent landing redirect is a fixed
  // server-side URL (Shopify__ClientBaseUrl + /integrations/shopify/ShopifyCreate)
  // that does NOT preserve query params — so on the way BACK we fall back to
  // checking for the sessionStorage resume marker instead, which does survive
  // the full-page redirect.
  const returnToOnboarding =
    query.get("returnTo") === "onboarding" ||
    (() => {
      try {
        return sessionStorage.getItem(PACKAGE_WIZARD_RESUME_KEY) != null;
      } catch {
        return false;
      }
    })();

  // Default behaviors — used only when the caller (Routes.tsx standalone
  // route, or an embedding parent) doesn't pass an explicit override.
  function defaultCancel() {
    if (returnToOnboarding) {
      // Marker is untouched — Packages page reopens the wizard at the
      // same step the user left it on.
      navigate("/admin/packages");
      return;
    }
    navigate("/integrations");
  }

  function defaultFinish() {
    if (returnToOnboarding) {
      try {
        const raw = sessionStorage.getItem(PACKAGE_WIZARD_RESUME_KEY);
        if (raw) {
          const parsed = JSON.parse(raw);
          sessionStorage.setItem(
            PACKAGE_WIZARD_RESUME_KEY,
            JSON.stringify({
              ...parsed,
              stepIndex: (parsed.stepIndex ?? 0) + 1
            })
          );
        }
      } catch (err) {
        console.error("Failed to advance package wizard resume marker:", err);
      }
      navigate("/admin/packages");
      return;
    }
    navigate("/integrations/shopify");
  }

  function defaultAdvancedSettings() {
    navigate("/integrations/shopify");
  }

  // OAuth callback — fires after Shopify redirects back
  useEffect(() => {
    if (query.get("error") === "true") {
      setOauthError(
        query.get("message") || "OAuth connection failed. Please try again."
      );
      return;
    }

    const shop = query.get("shop");
    if (!shop) return;

    setIsConnecting(false);
    setSending(false);
    setStep(2);

    // Auto-create default routing workflow if the site has none yet
    ensureDefaultRoutingWorkflow(api.messaging).then((result) => {
      if (result === "created") setDefaultWorkflowCreated(true);
    });

    // Recover the configId — React state is gone after the OAuth redirect
    api.messaging.get("Shopify/Integrations/").then(async (r) => {
      if (r.ok) {
        const configs = await r.json();
        if (Array.isArray(configs)) {
          const match = configs.find(
            (c: { domain: string; id: number }) => c.domain === shop
          );
          if (match) setConfigId(match.id);
        }
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  async function handleSubmit() {
    setSending(true);
    try {
      const response = await api.messaging.post(
        "Shopify/Auth/SaveCredentials",
        {
          storeName,
          domain,
          teamId: location.state?.teamId
        }
      );
      if (response.ok) {
        const result = await response.json();
        if (result.id) {
          setConfigId(result.id);
          // Chain straight into the OAuth grant so "Continue" is a single click —
          // the merchant never needs to press it twice for what looks like one action.
          await handleGrant(result.id);
          return;
        }
      }
    } catch (err) {
      console.error("Error saving Shopify credentials:", err);
    } finally {
      setSending(false);
    }
  }

  async function handleGrant(idOverride?: number) {
    const id = idOverride ?? configId;
    if (!id || id < 0) return;
    setSending(true);
    try {
      const response = await api.messaging.get(`Shopify/authorize/${id}`);
      const result = await response.json();
      if (response.ok && result.authorizationUrl) {
        setIsConnecting(true);
        window.location.href = result.authorizationUrl;
      }
    } catch (err) {
      console.error("Grant access failed", err);
    } finally {
      setSending(false);
    }
  }

  const resolvedCancel = onCancel ?? defaultCancel;
  const resolvedFinish = onFinish ?? defaultFinish;
  const resolvedAdvancedSettings =
    onAdvancedSettings ?? defaultAdvancedSettings;

  async function handleFinish() {
    if (selectedTeamId !== "") {
      setSavingTeam(true);
      try {
        // Set the selected team on the default Experience Bundle catch-all
        const defaultResp = await api.messaging.get("Bundle/default");
        if (defaultResp.ok) {
          const bundle = await defaultResp.json();
          if (bundle?.id) {
            await api.messaging.put(`Bundle/update/${bundle.id}`, {
              ...bundle,
              defaultTeamId: selectedTeamId
            });
          }
        }
      } catch (err) {
        console.error("Failed to set default team:", err);
      } finally {
        setSavingTeam(false);
      }
    }
    resolvedFinish();
  }

  const steps = [
    { n: 1, label: "Connect" },
    { n: 2, label: "You're live" }
  ];

  return (
    <SectionCard title="Connect Shopify">
      {oauthError && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <strong>Connection failed:</strong> {oauthError}
        </div>
      )}

      {/* Step indicator */}
      <div className="flex items-center gap-0 mb-8 mt-2 max-w-xs">
        {steps.map((s, i) => (
          <React.Fragment key={s.n}>
            <div className="flex items-center gap-2">
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${step >= s.n ? "bg-(--ds-primary) text-white" : "bg-zinc-200 text-zinc-500"}`}
              >
                {step > s.n ? <Check className="w-4 h-4" aria-hidden /> : s.n}
              </div>
              <span
                className={`text-sm ${step === s.n ? "font-semibold text-zinc-800" : "text-zinc-400"}`}
              >
                {s.label}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className="flex-1 h-px bg-zinc-200 mx-3" />
            )}
          </React.Fragment>
        ))}
      </div>

      {/* ── Step 1: Connect ── */}
      {step === 1 && (
        <div className="max-w-sm">
          <p className="text-sm text-zinc-500 mb-6">
            Enter your Shopify store URL to begin the OAuth connection.
          </p>

          <div className="space-y-4 mb-6">
            <FormRow label="Store name">
              <FieldInput
                value={storeName}
                placeholder="My Store"
                onChange={(e) => setStoreName(e.target.value)}
              />
            </FormRow>
            <FormRow label="Store URL">
              <FieldInput
                value={domain}
                placeholder="yourstore.myshopify.com"
                onChange={(e) => setDomain(e.target.value)}
              />
            </FormRow>
          </div>

          {/* First click saves credentials and gets config ID, second grants OAuth */}
          <div className="flex gap-2">
            <SecondaryButton onClick={resolvedCancel}>Cancel</SecondaryButton>
            {configId < 0 ? (
              <PrimaryButton
                onClick={handleSubmit}
                disabled={!domain || sending}
              >
                {sending ? "Saving…" : "Continue"}
              </PrimaryButton>
            ) : (
              <PrimaryButton onClick={() => handleGrant()} disabled={sending}>
                {sending ? "Connecting…" : "Connect to Shopify"}
              </PrimaryButton>
            )}
          </div>
        </div>
      )}

      {/* ── Step 2: You're live ── */}
      {step === 2 && (
        <div className="max-w-sm">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-11 h-11 bg-green-100 rounded-full flex items-center justify-center shrink-0">
              <Check className="w-5 h-5 text-green-600" aria-hidden />
            </div>
            <div>
              <div className="font-semibold text-zinc-800 flex items-center gap-2">
                Shopify connected
                <img src={ShopifyLogo} className="h-5 w-5 shrink-0" alt="" />
              </div>
              <div className="text-sm text-zinc-500">
                AI tools, order lookup, and product search are now live.
              </div>
            </div>
          </div>

          {/* Team picker — sets catch-all team immediately */}
          <SectionCard className="mb-6 !bg-zinc-50 space-y-3">
            <p className="text-sm font-medium text-zinc-700">
              Route incoming chats to:
            </p>
            <FieldInput
              as="select"
              value={selectedTeamId}
              onChange={(e) =>
                setSelectedTeamId(
                  e.target.value === "" ? "" : Number(e.target.value)
                )
              }
            >
              <option value="">Choose a team (recommended)</option>
              {(teams ?? []).map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                </option>
              ))}
            </FieldInput>
            <p className="text-xs text-zinc-400">
              This becomes the fallback for any chat that doesn't match a canvas
              workflow. You can change it any time in{" "}
              <a href="#/deployments" className="underline">
                Chat Experience
              </a>
              .
            </p>
          </SectionCard>

          {defaultWorkflowCreated && (
            <div className="mb-6 px-4 py-3 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-800">
              A disabled "Default Routing" workflow was added to your account.
              Enable it in{" "}
              <a href="#/workflows" className="underline font-medium">
                Workflows
              </a>{" "}
              when you're ready.
            </div>
          )}

          <div className="flex items-center gap-2">
            {savingTeam ? (
              <div className="flex items-center gap-2 text-sm text-zinc-500">
                <LoadingSpinner inline /> Saving…
              </div>
            ) : (
              <PrimaryButton onClick={handleFinish}>
                {selectedTeamId !== "" ? "Save & finish" : "Finish"}
              </PrimaryButton>
            )}
            <a
              href="#/integrations/shopify"
              className="text-sm text-zinc-400 hover:text-zinc-600 underline"
              onClick={(e) => {
                e.preventDefault();
                resolvedAdvancedSettings();
              }}
            >
              Configure advanced settings →
            </a>
          </div>
        </div>
      )}

      {/* Loading overlay while OAuth redirect is in progress */}
      {isConnecting && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/80 rounded-2xl z-50 gap-3">
          <LoadingSpinner />
          <span className="text-zinc-600 text-sm font-medium">
            Connecting to Shopify…
          </span>
        </div>
      )}
    </SectionCard>
  );
}

export function ShopifyBackToShopifyOverview() {
  const navigate = useNavigate();
  return (
    <div className="relative flex items-center">
      <button
        type="button"
        className="flex cursor-pointer items-center gap-1 text-zinc-600"
        onClick={() => navigate("/integrations")}
      >
        <ArrowLeft className="h-5 w-5" />
        Back
      </button>
      <span className="absolute left-1/2 transform -translate-x-1/2 font-semibold text-md">
        Integrations
      </span>
    </div>
  );
}
